import React, { useEffect, useState } from 'react';
import DENTModalOverviewSubButtonsSelection from '../DENTModalOverviewSubButtonsSelection/DENTModalOverviewSubButtonsSelection';
import {
	ToothJaw,
	ToothPathologyToothWear,
	ToothToothWearZone,
} from '../../../../graphql/graphql';
import useTooth from '../../Hooks/Tooth/useTooth';
import { useDENTContext } from '../../Services/DENTContext';

export enum ToothWearType {
	ABRASION = 'ABRASION',
	EROSION = 'EROSION',
	ATTRITION = 'ATTRITION',
	ABFRACTION = 'ABFRACTION',
}

export const SURFACE_BASED_TOOTH_WEAR_TYPES = [
	ToothWearType.EROSION,
	ToothWearType.ATTRITION,
	ToothWearType.ABFRACTION,
] as const;

export type SurfaceBasedToothWearType =
	(typeof SURFACE_BASED_TOOTH_WEAR_TYPES)[number];

export const isSurfaceBasedToothWearType = (
	type: ToothWearType,
): type is SurfaceBasedToothWearType =>
	SURFACE_BASED_TOOTH_WEAR_TYPES.includes(type as SurfaceBasedToothWearType);

interface Props {
	onTypeChange?: (type: ToothWearType | undefined) => void;
}

const DENTModalOverviewPathologyToothWear = ({ onTypeChange }: Props) => {
	const [type, setType] = useState<ToothWearType>();

	const { activeTooth } = useDENTContext();
	const {
		pathology: {
			toothWear: { abrasion, erosion, attrition, abfraction },
		},
	} = useTooth();

	const [values, setValues] = useState<ToothPathologyToothWear>({
		abrasion: {
			zones: abrasion.zones,
		},
		erosion: {
			surfaces: erosion.surfaces,
		},
		attrition: {
			surfaces: attrition.surfaces,
		},
		abfraction: {
			surfaces: abfraction.surfaces,
		},
	});

	useEffect(() => {
		if (values.abrasion) {
			abrasion.setAbrasion(values.abrasion);
		}
	}, [values]);

	const handleChangeType = (value: ToothWearType) => {
		const shouldReset = value === type;
		const newType = shouldReset ? undefined : value;
		setType(newType);

		onTypeChange?.(newType);

		if (shouldReset && value === ToothWearType.ABRASION) {
			setValues(prev => ({
				...prev,
				abrasion: {
					zones: [],
				},
			}));
		}
	};

	const handleChangeZone = (value: ToothToothWearZone) =>
		setValues(prev => {
			const zones = prev?.abrasion?.zones || [];

			return {
				...prev,
				abrasion: {
					...prev.abrasion,
					zones: zones.includes(value)
						? zones.filter(item => item !== value)
						: [...zones, value],
				},
			};
		});

	const rows = [
		{
			shouldHide: false,
			items: [
				{
					title:
						'patientTreatment.meddiDENT.dentalCross.pathologyModal.toothWear.typeErosion',
					isSelected: type === ToothWearType.EROSION,
					onClick: () => handleChangeType(ToothWearType.EROSION),
				},
				{
					title:
						'patientTreatment.meddiDENT.dentalCross.pathologyModal.toothWear.typeAttrition',
					isSelected: type === ToothWearType.ATTRITION,
					onClick: () => handleChangeType(ToothWearType.ATTRITION),
				},
				{
					title:
						'patientTreatment.meddiDENT.dentalCross.pathologyModal.toothWear.typeAbfraction',
					isSelected: type === ToothWearType.ABFRACTION,
					onClick: () => handleChangeType(ToothWearType.ABFRACTION),
				},
				{
					title:
						'patientTreatment.meddiDENT.dentalCross.pathologyModal.toothWear.typeAbrasion',
					isSelected: type === ToothWearType.ABRASION,
					onClick: () => handleChangeType(ToothWearType.ABRASION),
				},
			],
		},
		{
			shouldHide: type !== ToothWearType.ABRASION,
			items: [
				{
					title:
						'patientTreatment.meddiDENT.dentalCross.pathologyModal.toothWear.zoneBuccal',
					isSelected:
						values?.abrasion?.zones?.includes(ToothToothWearZone.Buccal) ??
						false,
					onClick: () => handleChangeZone(ToothToothWearZone.Buccal),
				},
				activeTooth?.jaw === ToothJaw.Lower
					? {
							title:
								'patientTreatment.meddiDENT.dentalCross.pathologyModal.toothWear.zoneLingual',
							isSelected:
								values?.abrasion?.zones?.includes(ToothToothWearZone.Lingual) ??
								false,
							onClick: () => handleChangeZone(ToothToothWearZone.Lingual),
					  }
					: {
							title:
								'patientTreatment.meddiDENT.dentalCross.pathologyModal.toothWear.zonePalatal',
							isSelected:
								values?.abrasion?.zones?.includes(ToothToothWearZone.Palatal) ??
								false,
							onClick: () => handleChangeZone(ToothToothWearZone.Palatal),
					  },
			],
		},
	];

	return <DENTModalOverviewSubButtonsSelection rows={rows} />;
};

export default DENTModalOverviewPathologyToothWear;
